import React, {useEffect, useMemo, useState} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {Delete, Edit, Save} from "@mui/icons-material";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";
import {
  createMarketPriceArea,
  createMarketPriceCommodity,
  createMarketPriceRow,
  deleteMarketPriceArea,
  deleteMarketPriceCommodity,
  deleteMarketPriceRow,
  fetchMarketPriceAreas,
  fetchMarketPriceCommodities,
  fetchMarketPrices,
  MarketPriceArea,
  MarketPriceCommodity,
  MarketPriceMode,
  MarketPriceRow,
  updateMarketPriceArea,
  updateMarketPriceCommodity,
  updateMarketPriceRow,
} from "../../api/marketPrice";

const MarketPricePage: React.FC = () => {
  const {user} = useAuth();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [areas, setAreas] = useState<MarketPriceArea[]>([]);
  const [commodities, setCommodities] = useState<MarketPriceCommodity[]>([]);
  const [priceRows, setPriceRows] = useState<MarketPriceRow[]>([]);

  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [selectedCommodityId, setSelectedCommodityId] = useState("");
  const [selectedShrimpType, setSelectedShrimpType] = useState("");

  const [areaForm, setAreaForm] = useState({id: "", name: ""});
  const [commodityForm, setCommodityForm] = useState<{id: string; name: string; defaultPricingMode: MarketPriceMode}>({
    id: "",
    name: "",
    defaultPricingMode: "COUNT_WISE",
  });
  const [priceForm, setPriceForm] = useState({
    id: "",
    commodityId: "",
    pricingMode: "COUNT_WISE" as MarketPriceMode,
    count: "",
    label: "",
    pricePerKg: "",
    isActive: true,
    isPublic: false,
  });

  const activeAreas = useMemo(() => areas.filter((a) => a.isActive), [areas]);
  const activeCommodities = useMemo(() => commodities.filter((c) => c.isActive), [commodities]);
  const selectedCommodity = useMemo(
    () => commodities.find((c) => c.id === selectedCommodityId) ?? null,
    [commodities, selectedCommodityId]
  );
  const selectedFormCommodityId = priceForm.commodityId || selectedCommodityId;
  const selectedFormCommodity = useMemo(
    () => commodities.find((c) => c.id === selectedFormCommodityId) ?? null,
    [commodities, selectedFormCommodityId]
  );

  const selectedPricingMode: MarketPriceMode = selectedCommodity?.defaultPricingMode ?? "COUNT_WISE";
  const formPricingMode: MarketPriceMode = selectedFormCommodity?.defaultPricingMode ?? selectedPricingMode;
  const isShrimpSelectedCommodity = (selectedCommodity?.name || "").toLowerCase().includes("shrimp");
  const isShrimpFormCommodity = (selectedFormCommodity?.name || "").toLowerCase().includes("shrimp");
  const shrimpTypes = useMemo(() => {
    const typeSet = new Set(
      priceRows
        .map((row) => (row.label || "").trim())
        .filter((value) => value.length > 0)
    );
    return Array.from(typeSet).sort((a, b) => a.localeCompare(b));
  }, [priceRows]);
  const filteredPriceRows = useMemo(() => {
    if (!isShrimpSelectedCommodity || !selectedShrimpType) {
      return priceRows;
    }
    return priceRows.filter((row) => (row.label || "").trim() === selectedShrimpType);
  }, [priceRows, isShrimpSelectedCommodity, selectedShrimpType]);
  const sortedPriceRows = useMemo(() => {
    const rows = [...filteredPriceRows];
    if (selectedPricingMode === "COUNT_WISE") {
      rows.sort((a, b) => (b.count || 0) - (a.count || 0));
    }
    return rows;
  }, [filteredPriceRows, selectedPricingMode]);
  const canSubmitPrice =
    !!selectedAreaId &&
    !!selectedFormCommodityId &&
    !!priceForm.pricePerKg &&
    (formPricingMode !== "COUNT_WISE" || !!priceForm.count) &&
    (!isShrimpFormCommodity || !!priceForm.label.trim());

  const loadMasters = async () => {
    setLoading(true);
    setError("");
    try {
      const [areasData, commoditiesData] = await Promise.all([fetchMarketPriceAreas(!isSuperAdmin), fetchMarketPriceCommodities(!isSuperAdmin)]);
      setAreas(areasData);
      setCommodities(commoditiesData);

      const activeArea = areasData.find((a) => a.isActive);
      const preferredShrimp = commoditiesData.find((c) => c.isActive && c.name.toLowerCase() === "shrimp");
      const activeCommodity = preferredShrimp || commoditiesData.find((c) => c.isActive);

      setSelectedAreaId((prev) => prev || activeArea?.id || "");
      setSelectedCommodityId((prev) => prev || activeCommodity?.id || "");
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load market price masters");
    } finally {
      setLoading(false);
    }
  };

  const loadPrices = async () => {
    if (!selectedAreaId || !selectedCommodityId) {
      setPriceRows([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const rows = await fetchMarketPrices({
        areaId: selectedAreaId,
        commodityId: selectedCommodityId,
        activeOnly: !isSuperAdmin,
      });
      setPriceRows(rows);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load market prices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMasters();
  }, []);

  useEffect(() => {
    loadPrices();
  }, [selectedAreaId, selectedCommodityId, isSuperAdmin]);

  useEffect(() => {
    if (!isShrimpSelectedCommodity) {
      setSelectedShrimpType("");
      return;
    }
    if (shrimpTypes.length === 0) {
      setSelectedShrimpType("");
      return;
    }
    setSelectedShrimpType((prev) => (prev && shrimpTypes.includes(prev) ? prev : shrimpTypes[0]));
  }, [isShrimpSelectedCommodity, shrimpTypes]);

  const resetAreaForm = () => setAreaForm({id: "", name: ""});
  const resetCommodityForm = () => setCommodityForm({id: "", name: "", defaultPricingMode: "COUNT_WISE"});
  const resetPriceForm = () =>
    setPriceForm({
      id: "",
      commodityId: selectedCommodityId,
      pricingMode: selectedPricingMode,
      count: "",
      label: "",
      pricePerKg: "",
      isActive: true,
      isPublic: false,
    });

  useEffect(() => {
    setPriceForm((prev) => {
      if (prev.id || prev.commodityId) {
        return prev;
      }
      return {
        ...prev,
        commodityId: selectedCommodityId,
        pricingMode: selectedPricingMode,
        count: "",
        label: "",
      };
    });
  }, [selectedCommodityId, selectedPricingMode]);

  const saveArea = async () => {
    if (!areaForm.name.trim()) return;
    try {
      if (areaForm.id) {
        await updateMarketPriceArea(areaForm.id, {name: areaForm.name.trim()});
      } else {
        await createMarketPriceArea({name: areaForm.name.trim(), isActive: true});
      }
      resetAreaForm();
      await loadMasters();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to save area");
    }
  };

  const saveCommodity = async () => {
    if (!commodityForm.name.trim()) return;
    try {
      if (commodityForm.id) {
        await updateMarketPriceCommodity(commodityForm.id, {
          name: commodityForm.name.trim(),
          defaultPricingMode: commodityForm.defaultPricingMode,
        });
      } else {
        await createMarketPriceCommodity({
          name: commodityForm.name.trim(),
          defaultPricingMode: commodityForm.defaultPricingMode,
          isActive: true,
        });
      }
      resetCommodityForm();
      await loadMasters();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to save commodity");
    }
  };

  const savePriceRow = async () => {
    const commodityId = priceForm.commodityId || selectedCommodityId;
    if (!selectedAreaId || !commodityId || !priceForm.pricePerKg) return;
    try {
      const rowMode = formPricingMode;
      const payload = {
        areaId: selectedAreaId,
        commodityId,
        pricingMode: rowMode,
        count: rowMode === "COUNT_WISE" ? Number(priceForm.count) : undefined,
        label: rowMode === "PER_KG" || isShrimpFormCommodity ? priceForm.label.trim() : undefined,
        pricePerKg: Number(priceForm.pricePerKg),
        isActive: priceForm.isActive,
        isPublic: priceForm.isPublic,
      };

      if (priceForm.id) {
        await updateMarketPriceRow(priceForm.id, payload);
      } else {
        await createMarketPriceRow(payload);
      }
      if (commodityId !== selectedCommodityId) {
        setSelectedCommodityId(commodityId);
      }
      resetPriceForm();
      await loadPrices();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to save market price row");
    }
  };

  const editPriceRow = (row: MarketPriceRow) => {
    setPriceForm({
      id: row.id,
      commodityId: row.commodityId,
      pricingMode: row.pricingMode,
      count: row.count ? String(row.count) : "",
      label: row.label || "",
      pricePerKg: String(row.pricePerKg),
      isActive: row.isActive,
      isPublic: !!row.isPublic,
    });
  };

  return (
    <Box sx={{p: 3}}>
      <Typography variant="h4" gutterBottom>
        Market Prices
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{mb: 2}}>
        Select area and commodity to view latest rates. Shrimp is default and shown as count-wise pricing.
      </Typography>

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      <Paper sx={{p: 2, mb: 3}}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Select Area</InputLabel>
              <Select label="Select Area" value={selectedAreaId} onChange={(e) => setSelectedAreaId(e.target.value)}>
                {activeAreas.map((area) => (
                  <MenuItem key={area.id} value={area.id}>
                    {area.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Commodity</InputLabel>
              <Select label="Commodity" value={selectedCommodityId} onChange={(e) => setSelectedCommodityId(e.target.value)}>
                {activeCommodities.map((commodity) => (
                  <MenuItem key={commodity.id} value={commodity.id}>
                    {commodity.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {isShrimpSelectedCommodity && shrimpTypes.length > 0 && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Shrimp Type</InputLabel>
                <Select label="Shrimp Type" value={selectedShrimpType} onChange={(e) => setSelectedShrimpType(e.target.value)}>
                  {shrimpTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Card sx={{mb: 3}}>
        <CardContent>
          <Box sx={{display: "flex", justifyContent: "space-between", mb: 1}}>
            <Typography variant="h6">
              Area: {activeAreas.find((a) => a.id === selectedAreaId)?.name || "-"}
            </Typography>
            <Chip color="primary" label="Today's Price per KG" />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{selectedPricingMode === "COUNT_WISE" ? "Count" : "Type"}</TableCell>
                  <TableCell>Today's Price</TableCell>
                  {isSuperAdmin && <TableCell>Status</TableCell>}
                  {isSuperAdmin && <TableCell>Public</TableCell>}
                  {isSuperAdmin && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPriceRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 5 : 2} align="center">
                      {loading ? "Loading..." : "No market prices configured"}
                    </TableCell>
                  </TableRow>
                )}
                {sortedPriceRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.pricingMode === "COUNT_WISE" ? `${row.count}C` : row.label || "-"}</TableCell>
                    <TableCell>₹ {Math.round(Number(row.pricePerKg))}</TableCell>
                    {isSuperAdmin && (
                      <TableCell>
                        <Chip size="small" label={row.isActive ? "Active" : "Inactive"} color={row.isActive ? "success" : "default"} />
                      </TableCell>
                    )}
                    {isSuperAdmin && (
                      <TableCell>
                        <Chip size="small" label={row.isPublic ? "Yes" : "No"} color={row.isPublic ? "primary" : "default"} />
                      </TableCell>
                    )}
                    {isSuperAdmin && (
                      <TableCell align="right">
                        <Button size="small" startIcon={<Edit />} onClick={() => editPriceRow(row)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={async () => {
                            await deleteMarketPriceRow(row.id);
                            await loadPrices();
                          }}>
                          Delete
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="info" sx={{mt: 2}}>
            We are committed to sharing the latest market prices, but values can change frequently based on buyer demand, quality, quantity,
            and local trading conditions.
          </Alert>
        </CardContent>
      </Card>

      {isSuperAdmin && (
        <Stack spacing={3}>
          <Divider />
          <Typography variant="h5">Super Admin Management</Typography>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Price Rows (COUNT_WISE / PER_KG) against Commodity
              </Typography>
              <Grid container spacing={1} sx={{mb: 2}}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Commodity</InputLabel>
                    <Select
                      value={priceForm.commodityId || selectedCommodityId}
                      label="Commodity"
                      onChange={(e) => {
                        const commodityId = e.target.value;
                        const mode = activeCommodities.find((c) => c.id === commodityId)?.defaultPricingMode ?? "COUNT_WISE";
                        setPriceForm((prev) => ({
                          ...prev,
                          commodityId,
                          pricingMode: mode,
                          count: "",
                          label: "",
                        }));
                      }}>
                      {activeCommodities.map((commodity) => (
                        <MenuItem key={commodity.id} value={commodity.id}>
                          {commodity.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Pricing Mode" value={formPricingMode} InputProps={{readOnly: true}} />
                </Grid>
                {formPricingMode === "COUNT_WISE" ? (
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Count"
                      value={priceForm.count}
                      onChange={(e) => setPriceForm({...priceForm, count: e.target.value})}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Label (e.g. Type/Grade)"
                      value={priceForm.label}
                      onChange={(e) => setPriceForm({...priceForm, label: e.target.value})}
                    />
                  </Grid>
                )}
                {isShrimpFormCommodity && formPricingMode === "COUNT_WISE" && (
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Shrimp Type (e.g. Vannamei)"
                      value={priceForm.label}
                      onChange={(e) => setPriceForm({...priceForm, label: e.target.value})}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Price (₹/kg)"
                    value={priceForm.pricePerKg}
                    onChange={(e) => setPriceForm({...priceForm, pricePerKg: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={2} sx={{display: "flex", alignItems: "center"}}>
                  <FormControlLabel
                    control={<Checkbox checked={priceForm.isPublic} onChange={(e) => setPriceForm({...priceForm, isPublic: e.target.checked})} />}
                    label="Public"
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{display: "flex", gap: 1}}>
                  <Button variant="contained" onClick={savePriceRow} disabled={!canSubmitPrice}>
                    {priceForm.id ? "Update Price" : "Add Price"}
                  </Button>
                  {priceForm.id && (
                    <Button variant="outlined" onClick={resetPriceForm}>
                      Cancel
                    </Button>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Divider />
          <Typography variant="h6">Master Data Management</Typography>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Area Master
              </Typography>
              <Stack direction="row" spacing={1} sx={{mb: 2}}>
                <TextField label="Area Name" value={areaForm.name} onChange={(e) => setAreaForm({...areaForm, name: e.target.value})} fullWidth />
                <Button variant="contained" onClick={saveArea} startIcon={<Save />}>
                  {areaForm.id ? "Update" : "Add"}
                </Button>
                {areaForm.id && (
                  <Button variant="outlined" onClick={resetAreaForm}>
                    Cancel
                  </Button>
                )}
              </Stack>
              <Table size="small">
                <TableBody>
                  {areas.map((area) => (
                    <TableRow key={area.id}>
                      <TableCell>{area.name}</TableCell>
                      <TableCell>{area.isActive ? "Active" : "Inactive"}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => setAreaForm({id: area.id, name: area.name})}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={async () => {
                            await deleteMarketPriceArea(area.id);
                            await loadMasters();
                          }}>
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Commodity Master
              </Typography>
              <Grid container spacing={1} sx={{mb: 2}}>
                <Grid item xs={12} md={5}>
                  <TextField
                    label="Commodity Name"
                    value={commodityForm.name}
                    onChange={(e) => setCommodityForm({...commodityForm, name: e.target.value})}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Default Pricing Mode</InputLabel>
                    <Select
                      value={commodityForm.defaultPricingMode}
                      label="Default Pricing Mode"
                      onChange={(e) => setCommodityForm({...commodityForm, defaultPricingMode: e.target.value as MarketPriceMode})}>
                      <MenuItem value="COUNT_WISE">COUNT_WISE</MenuItem>
                      <MenuItem value="PER_KG">PER_KG</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3} sx={{display: "flex", gap: 1}}>
                  <Button variant="contained" onClick={saveCommodity} startIcon={<Save />} fullWidth>
                    {commodityForm.id ? "Update" : "Add"}
                  </Button>
                  {commodityForm.id && (
                    <Button variant="outlined" onClick={resetCommodityForm}>
                      Cancel
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Table size="small">
                <TableBody>
                  {commodities.map((commodity) => (
                    <TableRow key={commodity.id}>
                      <TableCell>{commodity.name}</TableCell>
                      <TableCell>{commodity.defaultPricingMode}</TableCell>
                      <TableCell>{commodity.isActive ? "Active" : "Inactive"}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() =>
                            setCommodityForm({
                              id: commodity.id,
                              name: commodity.name,
                              defaultPricingMode: commodity.defaultPricingMode,
                            })
                          }>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={async () => {
                            await deleteMarketPriceCommodity(commodity.id);
                            await loadMasters();
                          }}>
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
};

export default MarketPricePage;
